import { fetchWithOptions } from 'Utility/fetch';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { formatPartialDate } from 'Utility/date';
import { TPatient, TProfileData, TLocation, TAvxLocationUser } from './interfaces';
import { responseTexts } from './config';
import { fetchAvxPatientInfo } from 'Store/readonly/fetchers';

/**
 * Search AVX for patient information based on the neuro patient
 */
const search = (serialNo: string): Promise<TPatient> => {
  return fetchWithOptions(
    `/api/resmed/v1/search`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: serialNo },
  ).then(
    (res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    },
    (err: Error) => {
      throw err;
    },
  );
};

/**
 * Create a link between the given neuro patient and AVX ECN
 */
const linkAvxPatient = (ecn: string): Promise<string | null> => {
  const body = { ecn: ecn };

  return fetchWithOptions(
    `/api/resmed/v1/link`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(body) },
  )
    .then(res => {
      if (res.status === 200) {
        return res.text();
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient link create'));
      return null;
    });
};

/**
 * Creates the patient to AVX external system and establishes a link with the created ECN and neuro patientId
 */
const createAvxPatient = (data: TProfileData): Promise<{ linkId: string; ecn: string } | string | null> => {
  const dataFormatted = { ...data, setupDate: formatPartialDate(data.setupDate) };

  return fetchWithOptions(
    `/api/resmed/v1/patient`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(dataFormatted) },
  )
    .then(async res => {
      if (res.status === 200) {
        return res.json();
      } else {
        const responseText = await res.text();

        if (res.status === 400 && responseTexts.includes(responseText)) {
          return responseText;
        }

        throw new Error('unexpected status ' + res.status);
      }
    })
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient create'));
      return null;
    });
};

/**
 * Update an existing patients data in AVX external system
 */
const updateAvxPatient = (data: { ecn: string } & Partial<TProfileData>): Promise<boolean> => {
  const dataFormatted = { ...data, setupDate: formatPartialDate(data.setupDate) };

  return fetchWithOptions(
    `/api/resmed/v1/patient`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify(dataFormatted) },
  )
    .then(res => {
      if (res.status === 200) {
        return true;
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient update'));
      return false;
    });
};

/**
 * Fetch organizations locations from AVX, locations are needed for AVX patient create route
 */
const fetchLocations = (): Promise<TLocation[] | null> => {
  return fetchWithOptions(`/api/resmed/v1/locations`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX location fetch'));
      return null;
    });
};

const fetchAvxLocationUsers = async (locationId: string): Promise<TAvxLocationUser[] | null> => {
  try {
    const res = await fetchWithOptions(
      `/api/resmed/v1/clinicalUsers/${locationId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );

    if (res.status !== 200) {
      makeLog(
        Loggable.BugActionable,
        new Error('unexpected status ' + res.status),
        new ActionableErrorContext(null, 'AVX location user fetch'),
      );
      return null;
    }

    const responseJson = await res.json();
    if (!responseJson || !Array.isArray(responseJson)) return null;

    return responseJson;
  } catch (error) {
    makeLog(
      Loggable.BugActionable,
      error instanceof Error ? error : new Error('strange error'),
      new ActionableErrorContext(null, 'AVX location user fetch'),
    );
    return null;
  }
};

export {
  fetchAvxPatientInfo,
  search,
  linkAvxPatient,
  createAvxPatient,
  updateAvxPatient,
  fetchLocations,
  fetchAvxLocationUsers,
};
